import React, { useEffect, useMemo, useState } from "react";

const CONTENT = {
  en: {
    nav: {
      proof: "Proof",
      problem: "Problem",
      solution: "Solution",
      products: "Platform",
      flow: "Flow",
      impact: "Impact",
      cases: "Case Studies",
      licensing: "Brazil Licensing",
      contact: "Contact",
    },
    hero: {
      eyebrow: "DatanexAI • Brazil Master Licensing Agent",
      title:
        "Eduboxx Turns Educational Technology Into Something Governments Can Actually See, Trust, and Deploy",
      subtitle:
        "Eduboxx is the physical deployment of a complete education technology system for Brazil — combining visible infrastructure, reliable content delivery, embedded intelligence, integrated workflows, and AI-powered learning in one unified platform.",
      primaryCta: "Request a Pilot",
      secondaryCta: "Explore Brazil Rollout",
      stats: [
        "Proven in Brazil",
        "Millions of streams delivered",
        "Thousands of students served",
        "Built for public-sector scale",
      ],
      sideTitle: "Why this message wins",
      sideBullets: [
        "It leads with something tangible and physically understandable.",
        "It transforms a complex platform into one clear deployment story.",
        "It proves the system has already worked at scale in Brazil.",
        "It creates urgency without sounding abstract or experimental.",
      ],
    },
    proof: {
      title: "Proven at Scale in Brazil",
      intro:
        "Eduboxx is not a concept. It has already been successfully implemented in major Brazilian education environments, delivering large-scale educational streaming and classroom access in real operational settings.",
      stats: [
        "Millions of streams delivered",
        "Thousands of students reached",
        "Multi-site deployment experience",
        "Validated in real education environments",
      ],
      institutionsTitle: "Implemented in",
      institutions: ["Prima", "LFG", "Damásio", "Anhanguera / Kroton"],
      description:
        "These deployments proved that Eduboxx could deliver reliable educational content at scale across complex learning environments — creating the foundation for the more powerful integrated DatanexAI model now being positioned for Brazil.",
    },
    problem: {
      title: "The Problem",
      intro:
        "Education systems want modernization, visibility, and better learning outcomes — but most initiatives fail long before they create real transformation.",
      cards: [
        {
          title: "Infrastructure Reality",
          text: "Many schools and networks still operate in environments where bandwidth, reliability, and simultaneous access remain major constraints.",
        },
        {
          title: "Fragmented Systems",
          text: "Content delivery, analytics, operations, and learning support are often spread across disconnected tools that create friction and confusion.",
        },
        {
          title: "Weak Procurement Narratives",
          text: "Public-sector buyers often struggle to trust or evaluate solutions that are presented only as software, dashboards, or cloud abstractions.",
        },
        {
          title: "Pilot-to-Scale Failure",
          text: "Even promising pilots often collapse because the model was never designed to become a scalable operating system.",
        },
      ],
    },
    solution: {
      title: "Why Eduboxx Wins",
      intro:
        "Eduboxx wins because it starts where trust starts: with something visible, physical, and deployable — then delivers the full platform inside that deployment.",
      cards: [
        {
          title: "Visible Infrastructure",
          text: "Eduboxx gives governments and institutions something tangible to evaluate, deploy, and expand.",
        },
        {
          title: "Integrated by Design",
          text: "EduInsights, EduFlux, and EduAIKit are not separate upsells. They are integrated capabilities inside the Eduboxx system.",
        },
        {
          title: "Operationally Clear",
          text: "One platform. One deployment path. One story from pilot to scale.",
        },
        {
          title: "Built for Expansion",
          text: "The model is designed to move from real implementations into regional, state, and national rollout opportunities.",
        },
      ],
    },
    products: {
      title: "Inside the Eduboxx Platform",
      intro:
        "Eduboxx is the visible deployment layer. Inside it sits a complete education technology ecosystem.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Physical Infrastructure",
          features: [
            "On-site deployment layer for schools and networks",
            "Visible, tangible infrastructure for public-sector confidence",
            "Supports large-scale content delivery in constrained environments",
            "Creates the physical gateway for full platform adoption",
          ],
        },
        {
          name: "EduInsights",
          tag: "Embedded Intelligence",
          features: [
            "Usage and adoption visibility",
            "Administrative dashboards and reporting",
            "Performance and engagement analytics",
            "System-wide insight for decision makers",
          ],
        },
        {
          name: "EduFlux",
          tag: "Embedded Orchestration",
          features: [
            "Workflow coordination across schools and programs",
            "Delivery logic across operational environments",
            "Content and rollout pathway management",
            "Scalable execution support",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Embedded AI Learning",
          features: [
            "AI-supported learning experiences",
            "Adaptive learning tools",
            "Innovation modules for modern classrooms",
            "Expansion from access into intelligent learning support",
          ],
        },
      ],
      closing:
        "Together, these components make Eduboxx far more than a delivery box. They make it the physical expression of a complete education operating system.",
    },
    flow: {
      title: "How the System Works",
      intro:
        "The Eduboxx model is designed to move from visible deployment to measurable educational and operational impact.",
      steps: [
        {
          title: "1. Deploy",
          text: "Install Eduboxx in pilot schools, campuses, classrooms, or target regions.",
        },
        {
          title: "2. Deliver",
          text: "Provide reliable access to educational content and services in real school environments.",
        },
        {
          title: "3. Monitor",
          text: "Use EduInsights to measure access, usage, engagement, and adoption.",
        },
        {
          title: "4. Coordinate",
          text: "Use EduFlux to manage operational workflows, programs, and scaling logic.",
        },
        {
          title: "5. Elevate Learning",
          text: "Use EduAIKit to extend the system into AI-powered learning support and modern educational experiences.",
        },
        {
          title: "6. Scale",
          text: "Expand from pilot implementation into network-wide, municipal, state, or national rollouts.",
        },
      ],
    },
    impact: {
      title: "Results and Impact",
      intro:
        "Eduboxx creates value at multiple levels: operationally, administratively, strategically, and educationally.",
      cards: [
        {
          title: "More Reliable Delivery",
          text: "Stabilize access to educational content across real-world school environments.",
        },
        {
          title: "Better Administrative Visibility",
          text: "Give leaders clearer insight into adoption, usage, and performance.",
        },
        {
          title: "Stronger System Coordination",
          text: "Unify delivery, workflows, and execution under one operational model.",
        },
        {
          title: "Greater Long-Term Transformation",
          text: "Create a practical bridge from visible infrastructure to AI-enabled educational modernization.",
        },
      ],
    },
    cases: {
      title: "Before and After",
      intro:
        "These scenarios show what changes when Eduboxx replaces fragmented educational delivery with an integrated deployment model.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Before: Institutions needed reliable large-scale educational video delivery across multiple environments, but infrastructure limitations made consistent performance difficult.",
          after:
            "After: Eduboxx supported large-scale streaming delivery, helping serve thousands of students and enabling millions of educational streams in real learning environments.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Before: Large educational networks needed a scalable way to deliver content across multiple sites with varying operational realities.",
          after:
            "After: Eduboxx created a stable and scalable infrastructure model capable of supporting simultaneous access across distributed educational environments.",
        },
        {
          title: "From Fragmentation to System",
          before:
            "Before: Delivery, operations, and visibility were disconnected, making scale difficult and procurement harder to justify.",
          after:
            "After: Eduboxx created one understandable deployment story — visible infrastructure outside, integrated intelligence and learning power inside.",
        },
      ],
    },
    licensing: {
      title: "Brazil Licensing Structure",
      body:
        "DatanexAI acts as the master licensing agent for Brazil, leading with Eduboxx as the visible and physically demonstrable education system while delivering the integrated value of EduInsights, EduFlux, and EduAIKit as part of the same platform.",
      bullets: [
        "One system, one procurement story",
        "Physical deployment with integrated digital intelligence",
        "Proven foundation for Brazil market entry",
        "Scalable from pilot to large public-sector rollout",
      ],
    },
    contact: {
      title: "Let’s Build the Right Brazil Deployment",
      intro:
        "Start a conversation around pilots, regional implementations, licensing, or large-scale public-sector education deployment in Brazil.",
      form: {
        name: "Name",
        org: "Organization",
        email: "Email",
        region: "Country / Region",
        interest: "Primary Interest",
        message: "Message",
        options: [
          "Eduboxx Pilot",
          "Government Deployment",
          "Regional Rollout",
          "Licensing Partnership",
        ],
        submit: "Submit Inquiry",
      },
      note:
        "This front-end form is ready to connect to your CRM, backend endpoint, or WordPress handler.",
    },
    footer: {
      left: "DatanexAI",
      right: "Brazil master licensing agent for the Eduboxx integrated education system.",
    },
  },

  pt: {
    nav: {
      proof: "Prova",
      problem: "Problema",
      solution: "Solução",
      products: "Plataforma",
      flow: "Fluxo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamento Brasil",
      contact: "Contato",
    },
    hero: {
      eyebrow: "DatanexAI • Agente Mestre de Licenciamento no Brasil",
      title:
        "Eduboxx Transforma Tecnologia Educacional em Algo que o Governo Pode Ver, Confiar e Implantar",
      subtitle:
        "Eduboxx é a implantação física de um sistema completo de tecnologia educacional para o Brasil — combinando infraestrutura visível, entrega confiável de conteúdo, inteligência integrada, fluxos operacionais e aprendizagem com IA em uma única plataforma.",
      primaryCta: "Solicitar Piloto",
      secondaryCta: "Explorar Rollout no Brasil",
      stats: [
        "Comprovado no Brasil",
        "Milhões de streams entregues",
        "Milhares de estudantes atendidos",
        "Feito para escala pública",
      ],
      sideTitle: "Por que essa mensagem vence",
      sideBullets: [
        "Ela lidera com algo tangível e fisicamente compreensível.",
        "Ela transforma uma plataforma complexa em uma narrativa clara de implantação.",
        "Ela prova que o sistema já funcionou em escala no Brasil.",
        "Ela cria urgência sem soar abstrata ou experimental.",
      ],
    },
    proof: {
      title: "Comprovado em Escala no Brasil",
      intro:
        "Eduboxx não é conceito. Já foi implementado com sucesso em importantes ambientes educacionais brasileiros, entregando streaming educacional em larga escala e acesso em sala de aula em operações reais.",
      stats: [
        "Milhões de streams entregues",
        "Milhares de estudantes alcançados",
        "Experiência em múltiplas unidades",
        "Validado em ambientes educacionais reais",
      ],
      institutionsTitle: "Implementado em",
      institutions: ["Prima", "LFG", "Damásio", "Anhanguera / Kroton"],
      description:
        "Essas implantações comprovaram a capacidade do Eduboxx de entregar conteúdo educacional com confiabilidade e escala em ambientes complexos — criando a base para o modelo integrado DatanexAI agora posicionado para o Brasil.",
    },
    problem: {
      title: "O Problema",
      intro:
        "Os sistemas educacionais querem modernização, visibilidade e melhores resultados — mas a maioria das iniciativas falha muito antes de criar transformação real.",
      cards: [
        {
          title: "Realidade de Infraestrutura",
          text: "Muitas escolas e redes ainda operam em ambientes onde banda, confiabilidade e acesso simultâneo continuam sendo grandes limitações.",
        },
        {
          title: "Sistemas Fragmentados",
          text: "Entrega de conteúdo, analytics, operações e apoio à aprendizagem costumam estar espalhados em ferramentas desconectadas.",
        },
        {
          title: "Narrativas Fracas para Compras Públicas",
          text: "Compradores do setor público frequentemente têm dificuldade para confiar em soluções apresentadas apenas como software ou abstrações em nuvem.",
        },
        {
          title: "Fracasso do Piloto para a Escala",
          text: "Mesmo pilotos promissores desmoronam porque o modelo nunca foi desenhado para se tornar um sistema operacional escalável.",
        },
      ],
    },
    solution: {
      title: "Por que Eduboxx Vence",
      intro:
        "Eduboxx vence porque começa onde a confiança começa: com algo visível, físico e implantável — e entrega a plataforma completa dentro dessa implantação.",
      cards: [
        {
          title: "Infraestrutura Visível",
          text: "Eduboxx oferece ao governo e às instituições algo tangível para avaliar, implantar e expandir.",
        },
        {
          title: "Integrado por Design",
          text: "EduInsights, EduFlux e EduAIKit não são upsells separados. São capacidades integradas dentro do sistema Eduboxx.",
        },
        {
          title: "Clareza Operacional",
          text: "Uma plataforma. Um caminho de implantação. Uma história do piloto à escala.",
        },
        {
          title: "Feito para Expandir",
          text: "O modelo foi desenhado para sair de implementações reais e chegar a rollouts regionais, estaduais e nacionais.",
        },
      ],
    },
    products: {
      title: "Dentro da Plataforma Eduboxx",
      intro:
        "Eduboxx é a camada visível de implantação. Dentro dele existe um ecossistema completo de tecnologia educacional.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestrutura Física",
          features: [
            "Camada local de implantação para escolas e redes",
            "Infraestrutura visível e tangível para confiança do setor público",
            "Suporta entrega em escala em ambientes restritos",
            "Cria a porta de entrada física para adoção da plataforma completa",
          ],
        },
        {
          name: "EduInsights",
          tag: "Inteligência Integrada",
          features: [
            "Visibilidade de uso e adoção",
            "Dashboards administrativos e relatórios",
            "Analytics de desempenho e engajamento",
            "Visão sistêmica para tomadores de decisão",
          ],
        },
        {
          name: "EduFlux",
          tag: "Orquestração Integrada",
          features: [
            "Coordenação de fluxos entre escolas e programas",
            "Lógica operacional de entrega",
            "Gestão de conteúdo e caminhos de rollout",
            "Suporte à execução escalável",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizagem com IA Integrada",
          features: [
            "Experiências educacionais com suporte de IA",
            "Ferramentas adaptativas",
            "Módulos de inovação para salas modernas",
            "Expansão do acesso para suporte inteligente à aprendizagem",
          ],
        },
      ],
      closing:
        "Juntos, esses componentes fazem do Eduboxx muito mais do que uma caixa de entrega. Fazem dele a expressão física de um sistema operacional educacional completo.",
    },
    flow: {
      title: "Como o Sistema Funciona",
      intro:
        "O modelo Eduboxx foi criado para sair da implantação visível e chegar a impacto mensurável.",
      steps: [
        { title: "1. Implantar", text: "Instale Eduboxx em escolas-piloto, campi, salas ou regiões-alvo." },
        { title: "2. Entregar", text: "Forneça acesso confiável a conteúdo e serviços educacionais em ambientes reais." },
        { title: "3. Monitorar", text: "Use EduInsights para medir acesso, uso, engajamento e adoção." },
        { title: "4. Coordenar", text: "Use EduFlux para gerenciar fluxos operacionais, programas e lógica de escala." },
        { title: "5. Elevar a Aprendizagem", text: "Use EduAIKit para ampliar o sistema para aprendizagem com IA." },
        { title: "6. Escalar", text: "Expanda do piloto para redes, municípios, estados ou rollout nacional." },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx cria valor em vários níveis: operacional, administrativo, estratégico e educacional.",
      cards: [
        { title: "Entrega Mais Confiável", text: "Estabiliza o acesso ao conteúdo educacional em ambientes reais." },
        { title: "Mais Visibilidade Administrativa", text: "Dá aos líderes uma visão mais clara de adoção, uso e desempenho." },
        { title: "Coordenação Mais Forte", text: "Unifica entrega, fluxos e execução em um só modelo operacional." },
        { title: "Transformação de Longo Prazo", text: "Cria uma ponte prática entre infraestrutura visível e modernização educacional com IA." },
      ],
    },
    cases: {
      title: "Antes e Depois",
      intro:
        "Esses cenários mostram o que muda quando Eduboxx substitui uma entrega fragmentada por um modelo integrado.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Antes: As instituições precisavam de entrega de vídeo educacional em larga escala, mas limitações de infraestrutura dificultavam a consistência.",
          after:
            "Depois: Eduboxx suportou streaming em escala, ajudando a servir milhares de estudantes e viabilizando milhões de streams em ambientes reais.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Antes: Grandes redes educacionais precisavam de uma forma escalável de entregar conteúdo em múltiplas unidades.",
          after:
            "Depois: Eduboxx criou um modelo estável e escalável capaz de suportar acesso simultâneo em ambientes distribuídos.",
        },
        {
          title: "Da Fragmentação ao Sistema",
          before:
            "Antes: Entrega, operações e visibilidade eram desconectadas, dificultando escala e justificativa de compra.",
          after:
            "Depois: Eduboxx criou uma narrativa única de implantação — infraestrutura visível por fora, inteligência integrada por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estrutura de Licenciamento no Brasil",
      body:
        "A DatanexAI atua como agente mestre de licenciamento no Brasil, liderando com Eduboxx como sistema educacional visível e fisicamente demonstrável, enquanto entrega o valor integrado de EduInsights, EduFlux e EduAIKit na mesma plataforma.",
      bullets: [
        "Um sistema, uma narrativa de compra",
        "Implantação física com inteligência digital integrada",
        "Base comprovada para entrada no mercado brasileiro",
        "Escalável do piloto ao grande rollout público",
      ],
    },
    contact: {
      title: "Vamos Construir a Implantação Certa no Brasil",
      intro:
        "Inicie uma conversa sobre pilotos, implementações regionais, licenciamento ou implantação educacional em larga escala no Brasil.",
      form: {
        name: "Nome",
        org: "Organização",
        email: "Email",
        region: "País / Região",
        interest: "Interesse Principal",
        message: "Mensagem",
        options: [
          "Piloto Eduboxx",
          "Implantação Governamental",
          "Rollout Regional",
          "Parceria de Licenciamento",
        ],
        submit: "Enviar Consulta",
      },
      note:
        "Este formulário está pronto para conexão com seu CRM, backend ou WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente mestre de licenciamento no Brasil para o sistema educacional integrado Eduboxx.",
    },
  },

  es: {
    nav: {
      proof: "Prueba",
      problem: "Problema",
      solution: "Solución",
      products: "Plataforma",
      flow: "Flujo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamiento Brasil",
      contact: "Contacto",
    },
    hero: {
      eyebrow: "DatanexAI • Agente Maestro de Licenciamiento en Brasil",
      title:
        "Eduboxx Convierte la Tecnología Educativa en Algo que los Gobiernos Pueden Ver, Confiar y Desplegar",
      subtitle:
        "Eduboxx es el despliegue físico de un sistema completo de tecnología educativa para Brasil, combinando infraestructura visible, entrega confiable de contenido, inteligencia integrada, flujos operativos y aprendizaje con IA en una sola plataforma.",
      primaryCta: "Solicitar Piloto",
      secondaryCta: "Explorar Despliegue Brasil",
      stats: [
        "Probado en Brasil",
        "Millones de streams entregados",
        "Miles de estudiantes atendidos",
        "Diseñado para escala pública",
      ],
      sideTitle: "Por qué este mensaje gana",
      sideBullets: [
        "Lidera con algo tangible y físicamente comprensible.",
        "Convierte una plataforma compleja en una historia clara de despliegue.",
        "Demuestra que el sistema ya funcionó a escala en Brasil.",
        "Crea urgencia sin sonar abstracto ni experimental.",
      ],
    },
    proof: {
      title: "Probado a Escala en Brasil",
      intro:
        "Eduboxx no es un concepto. Ya fue implementado con éxito en importantes entornos educativos brasileños, entregando streaming educativo a gran escala y acceso real en aulas.",
      stats: [
        "Millones de streams entregados",
        "Miles de estudiantes alcanzados",
        "Experiencia multi-sede",
        "Validado en entornos educativos reales",
      ],
      institutionsTitle: "Implementado en",
      institutions: ["Prima", "LFG", "Damásio", "Anhanguera / Kroton"],
      description:
        "Estas implementaciones demostraron la capacidad de Eduboxx para entregar contenido educativo con confiabilidad y escala en entornos complejos, creando la base del modelo integrado DatanexAI ahora posicionado para Brasil.",
    },
    problem: {
      title: "El Problema",
      intro:
        "Los sistemas educativos quieren modernización, visibilidad y mejores resultados, pero la mayoría de las iniciativas fracasan mucho antes de generar transformación real.",
      cards: [
        {
          title: "Realidad de Infraestructura",
          text: "Muchas escuelas y redes aún operan en entornos donde el ancho de banda, la confiabilidad y el acceso simultáneo siguen siendo limitaciones importantes.",
        },
        {
          title: "Sistemas Fragmentados",
          text: "La entrega de contenido, la analítica, las operaciones y el apoyo al aprendizaje suelen estar repartidos entre herramientas desconectadas.",
        },
        {
          title: "Narrativas Débiles para la Compra Pública",
          text: "Los compradores del sector público suelen desconfiar de soluciones presentadas solo como software o abstracciones en la nube.",
        },
        {
          title: "Fracaso del Piloto a la Escala",
          text: "Incluso pilotos prometedores colapsan porque el modelo nunca fue diseñado para convertirse en un sistema escalable.",
        },
      ],
    },
    solution: {
      title: "Por qué Eduboxx Gana",
      intro:
        "Eduboxx gana porque empieza donde empieza la confianza: con algo visible, físico y desplegable, y entrega la plataforma completa dentro de ese despliegue.",
      cards: [
        { title: "Infraestructura Visible", text: "Da a gobiernos e instituciones algo tangible para evaluar, desplegar y ampliar." },
        { title: "Integrado por Diseño", text: "EduInsights, EduFlux y EduAIKit no son upsells separados. Son capacidades integradas dentro del sistema." },
        { title: "Claridad Operativa", text: "Una plataforma. Una ruta de despliegue. Una historia desde el piloto hasta la escala." },
        { title: "Hecho para Expandirse", text: "El modelo fue diseñado para pasar de implementaciones reales a despliegues regionales y nacionales." },
      ],
    },
    products: {
      title: "Dentro de la Plataforma Eduboxx",
      intro:
        "Eduboxx es la capa visible de despliegue. Dentro de ella existe un ecosistema completo de tecnología educativa.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestructura Física",
          features: [
            "Capa local de despliegue para escuelas y redes",
            "Infraestructura visible y tangible para confianza pública",
            "Soporta entrega a escala en entornos restringidos",
            "Crea la puerta de entrada física para la adopción total",
          ],
        },
        {
          name: "EduInsights",
          tag: "Inteligencia Integrada",
          features: [
            "Visibilidad de uso y adopción",
            "Paneles administrativos y reportes",
            "Analítica de desempeño y participación",
            "Visión sistémica para decisores",
          ],
        },
        {
          name: "EduFlux",
          tag: "Orquestación Integrada",
          features: [
            "Coordinación de flujos entre escuelas y programas",
            "Lógica operativa de entrega",
            "Gestión de contenido y rutas de despliegue",
            "Soporte de ejecución escalable",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizaje con IA Integrado",
          features: [
            "Experiencias educativas con soporte de IA",
            "Herramientas adaptativas",
            "Módulos de innovación para aulas modernas",
            "Expansión del acceso hacia el apoyo inteligente al aprendizaje",
          ],
        },
      ],
      closing:
        "Juntos, estos componentes convierten a Eduboxx en mucho más que una caja de entrega. Lo convierten en la expresión física de un sistema operativo educativo completo.",
    },
    flow: {
      title: "Cómo Funciona el Sistema",
      intro:
        "El modelo Eduboxx está diseñado para pasar del despliegue visible al impacto medible.",
      steps: [
        { title: "1. Desplegar", text: "Instale Eduboxx en escuelas piloto, campus, aulas o regiones objetivo." },
        { title: "2. Entregar", text: "Proporcione acceso confiable a contenido y servicios educativos en entornos reales." },
        { title: "3. Monitorear", text: "Use EduInsights para medir acceso, uso, participación y adopción." },
        { title: "4. Coordinar", text: "Use EduFlux para gestionar flujos operativos, programas y lógica de escala." },
        { title: "5. Elevar el Aprendizaje", text: "Use EduAIKit para ampliar el sistema hacia el aprendizaje impulsado por IA." },
        { title: "6. Escalar", text: "Expanda desde el piloto hasta redes, municipios, estados o despliegues nacionales." },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx crea valor en varios niveles: operativo, administrativo, estratégico y educativo.",
      cards: [
        { title: "Entrega Más Confiable", text: "Estabiliza el acceso al contenido educativo en entornos reales." },
        { title: "Mayor Visibilidad Administrativa", text: "Da a los líderes una visión más clara de adopción, uso y desempeño." },
        { title: "Coordinación Más Fuerte", text: "Unifica entrega, flujos y ejecución bajo un solo modelo." },
        { title: "Transformación de Largo Plazo", text: "Crea un puente práctico entre infraestructura visible y modernización educativa con IA." },
      ],
    },
    cases: {
      title: "Antes y Después",
      intro:
        "Estos escenarios muestran qué cambia cuando Eduboxx reemplaza una entrega fragmentada por un modelo integrado.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Antes: Las instituciones necesitaban entrega de video educativo a gran escala, pero las limitaciones de infraestructura dificultaban la consistencia.",
          after:
            "Después: Eduboxx soportó streaming a escala, ayudando a atender miles de estudiantes y habilitando millones de streams en entornos reales.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Antes: Grandes redes educativas necesitaban una forma escalable de entregar contenido en múltiples sedes.",
          after:
            "Después: Eduboxx creó un modelo estable y escalable capaz de soportar acceso simultáneo en entornos distribuidos.",
        },
        {
          title: "De la Fragmentación al Sistema",
          before:
            "Antes: La entrega, las operaciones y la visibilidad estaban desconectadas, dificultando la escala y la justificación de compra.",
          after:
            "Después: Eduboxx creó una narrativa única de despliegue: infraestructura visible por fuera, inteligencia integrada por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estructura de Licenciamiento en Brasil",
      body:
        "DatanexAI actúa como agente maestro de licenciamiento para Brasil, liderando con Eduboxx como sistema educativo visible y físicamente demostrable, mientras entrega el valor integrado de EduInsights, EduFlux y EduAIKit en la misma plataforma.",
      bullets: [
        "Un sistema, una historia de compra",
        "Despliegue físico con inteligencia digital integrada",
        "Base probada para entrada al mercado brasileño",
        "Escalable desde el piloto hasta grandes despliegues públicos",
      ],
    },
    contact: {
      title: "Construyamos el Despliegue Correcto en Brasil",
      intro:
        "Inicie una conversación sobre pilotos, implementaciones regionales, licenciamiento o despliegue educativo a gran escala en Brasil.",
      form: {
        name: "Nombre",
        org: "Organización",
        email: "Email",
        region: "País / Región",
        interest: "Interés Principal",
        message: "Mensaje",
        options: [
          "Piloto Eduboxx",
          "Despliegue Gubernamental",
          "Despliegue Regional",
          "Socio de Licenciamiento",
        ],
        submit: "Enviar Consulta",
      },
      note:
        "Este formulario está listo para conectarse con su CRM, backend o WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente maestro de licenciamiento en Brasil para el sistema educativo integrado Eduboxx.",
    },
  },
};

function usePageStyles() {
  useEffect(() => {
    const styleId = "datanexai-v5-light-upgraded-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      * { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body {
        margin: 0;
        font-family: Inter, Arial, Helvetica, sans-serif;
        background:
          radial-gradient(circle at top left, rgba(59, 130, 246, 0.10), transparent 30%),
          radial-gradient(circle at top right, rgba(6, 182, 212, 0.08), transparent 28%),
          linear-gradient(180deg, #f8fbff 0%, #eef6ff 42%, #e8f2fb 100%);
        color: #0f172a;
      }
      a { color: inherit; text-decoration: none; }
      .page-shell { min-height: 100vh; }
      .container {
        width: min(1180px, calc(100% - 40px));
        margin: 0 auto;
      }

      .nav {
        position: sticky;
        top: 0;
        z-index: 100;
        backdrop-filter: blur(14px);
        background: rgba(255, 255, 255, 0.88);
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
      }
      .nav-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 18px;
        padding: 16px 0;
      }
      .brand-wrap {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .brand {
        font-size: 1.05rem;
        font-weight: 900;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }
      .brand-sub {
        font-size: 0.76rem;
        color: rgba(15, 23, 42, 0.62);
      }
      .nav-links {
        display: flex;
        align-items: center;
        gap: 14px;
        flex-wrap: wrap;
        color: rgba(15, 23, 42, 0.82);
        font-size: 0.92rem;
      }

      .lang-switcher {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px;
        border-radius: 999px;
        background: rgba(255,255,255,0.9);
        border: 1px solid rgba(15, 23, 42, 0.08);
      }
      .lang-btn {
        border: 0;
        background: transparent;
        color: rgba(15, 23, 42, 0.72);
        padding: 8px 12px;
        border-radius: 999px;
        cursor: pointer;
        font-weight: 800;
      }
      .lang-btn.active {
        background: linear-gradient(135deg, #2563eb, #0891b2);
        color: #fff;
      }

      .hero {
        padding: 96px 0 72px;
      }
      .hero-grid {
        display: grid;
        grid-template-columns: 1.25fr 0.85fr;
        gap: 28px;
        align-items: stretch;
      }
      .eyebrow {
        display: inline-flex;
        align-items: center;
        padding: 10px 14px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.10);
        border: 1px solid rgba(37, 99, 235, 0.16);
        color: #1d4ed8;
        font-size: 0.82rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        margin-bottom: 18px;
      }
      h1 {
        margin: 0 0 18px;
        font-size: clamp(2.45rem, 4vw, 4.5rem);
        line-height: 1.02;
        letter-spacing: -0.035em;
        color: #0f172a;
      }
      .lead {
        margin: 0 0 32px;
        font-size: 1.09rem;
        line-height: 1.8;
        color: rgba(15, 23, 42, 0.78);
        max-width: 760px;
      }

      .cta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 14px;
        margin-bottom: 30px;
      }
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 16px;
        padding: 15px 20px;
        font-size: 0.99rem;
        font-weight: 800;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      }
      .btn:hover {
        transform: translateY(-1px);
      }
      .btn-primary {
        background: linear-gradient(135deg, #1d4ed8 0%, #0891b2 100%);
        color: #fff;
        box-shadow: 0 16px 32px rgba(29, 78, 216, 0.22);
      }
      .btn-secondary {
        background: #ffffff;
        color: #0f172a;
        border: 1px solid rgba(15, 23, 42, 0.12);
        box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
      }

      .glass-card,
      .card,
      .feature-card,
      .step-card,
      .case-card,
      .contact-card,
      .licensing-box,
      .proof-band {
        background: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 24px;
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.07);
      }

      .glass-card,
      .proof-band,
      .licensing-box {
        padding: 26px;
      }

      .mini-kicker {
        color: #0369a1;
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 800;
        margin-bottom: 10px;
      }
      .card-title {
        margin: 0 0 14px;
        font-size: 1.32rem;
        font-weight: 800;
        color: #0f172a;
      }

      .bullet-list,
      .feature-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 12px;
      }

      .bullet-list li,
      .stat-item,
      .feature-list li,
      .institution-pill,
      .proof-stat {
        padding: 14px 16px;
        border-radius: 16px;
        background: #f3f8ff;
        color: #0f172a;
        line-height: 1.55;
      }

      section {
        padding: 42px 0 64px;
      }
      .section-header {
        max-width: 930px;
        margin-bottom: 26px;
      }
      .section-header h2 {
        margin: 0 0 14px;
        font-size: clamp(1.85rem, 3vw, 3rem);
        letter-spacing: -0.03em;
        color: #0f172a;
      }
      .section-header p {
        margin: 0;
        color: rgba(15, 23, 42, 0.72);
        line-height: 1.8;
        font-size: 1.03rem;
      }

      .cards-3,
      .cards-4,
      .steps-grid,
      .contact-grid,
      .cases-grid,
      .proof-grid {
        display: grid;
        gap: 20px;
      }
      .cards-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .cards-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .steps-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .cases-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .proof-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .contact-grid {
        grid-template-columns: 0.92fr 1.08fr;
        align-items: start;
      }

      .card,
      .feature-card,
      .step-card,
      .case-card,
      .contact-card {
        padding: 24px;
      }
      .card h3,
      .feature-card h3,
      .step-card h3,
      .case-card h3,
      .contact-card h3 {
        margin: 0 0 12px;
        font-size: 1.18rem;
        color: #0f172a;
      }
      .card p,
      .feature-card p,
      .step-card p,
      .case-card p,
      .contact-card p {
        margin: 0;
        color: rgba(15, 23, 42, 0.78);
        line-height: 1.7;
      }

      .feature-tag {
        display: inline-block;
        margin-bottom: 10px;
        padding: 7px 10px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.10);
        color: #1d4ed8;
        font-size: 0.75rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .section-closing,
      .proof-description {
        margin-top: 22px;
        color: rgba(15, 23, 42, 0.74);
        line-height: 1.75;
      }

      .proof-band {
        margin-top: 22px;
      }
      .institutions-row {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 18px;
      }
      .institution-pill {
        display: inline-flex;
        align-items: center;
        font-weight: 800;
      }

      .before-after {
        display: grid;
        gap: 12px;
        margin-top: 14px;
      }
      .before-box,
      .after-box {
        padding: 14px 16px;
        border-radius: 16px;
        background: #f3f8ff;
        color: #0f172a;
      }
      .before-label,
      .after-label {
        display: block;
        font-weight: 800;
        margin-bottom: 6px;
        color: #0f172a;
      }

      .licensing-box {
        padding: 26px;
        background: linear-gradient(135deg, rgba(219, 234, 254, 0.95), rgba(236, 254, 255, 0.95));
      }
      .licensing-box p {
        margin: 0 0 18px;
        color: rgba(15, 23, 42, 0.82);
        line-height: 1.8;
      }
      .licensing-points {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }
      .licensing-point {
        padding: 14px 16px;
        border-radius: 16px;
        background: rgba(255,255,255,0.78);
        color: #0f172a;
      }

      form {
        display: grid;
        gap: 14px;
      }
      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      label {
        display: grid;
        gap: 8px;
        font-size: 0.92rem;
        color: #0f172a;
        font-weight: 650;
      }
      input, select, textarea {
        width: 100%;
        border: 1px solid rgba(15, 23, 42, 0.12);
        background: rgba(255, 255, 255, 0.98);
        color: #0f172a;
        border-radius: 16px;
        padding: 14px 15px;
        font: inherit;
        outline: none;
      }
      input:focus, select:focus, textarea:focus {
        border-color: rgba(29, 78, 216, 0.35);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10);
      }
      textarea {
        min-height: 150px;
        resize: vertical;
      }
      .form-note {
        margin-top: 16px;
        font-size: 0.9rem;
        color: rgba(15, 23, 42, 0.62);
        line-height: 1.65;
      }

      footer {
        padding: 28px 0 46px;
        color: rgba(15, 23, 42, 0.62);
      }
      .footer-box {
        padding-top: 20px;
        border-top: 1px solid rgba(15, 23, 42, 0.08);
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 12px 16px;
      }

      @media (max-width: 1040px) {
        .hero-grid,
        .cards-4,
        .cards-3,
        .steps-grid,
        .cases-grid,
        .contact-grid,
        .proof-grid,
        .stats-grid,
        .licensing-points {
          grid-template-columns: 1fr;
        }
      }
      @media (max-width: 820px) {
        .nav-inner {
          flex-direction: column;
          align-items: flex-start;
        }
        .field-grid {
          grid-template-columns: 1fr;
        }
        .hero {
          padding-top: 52px;
          padding-bottom: 52px;
        }
        section {
          padding: 34px 0 52px;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

export default function App() {
  usePageStyles();

  const [lang, setLang] = useState("en");
  const [formData, setFormData] = useState({
    name: "",
    org: "",
    email: "",
    region: "",
    interest: "",
    message: "",
  });

  const t = useMemo(() => CONTENT[lang], [lang]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(
      lang === "pt"
        ? "Formulário pronto para integração com seu backend."
        : lang === "es"
        ? "Formulario listo para integrarse con su backend."
        : "Form ready to connect to your backend."
    );
  };

  const beforeLabel = lang === "pt" ? "Antes" : lang === "es" ? "Antes" : "Before";
  const afterLabel = lang === "pt" ? "Depois" : lang === "es" ? "Después" : "After";
  const sideKicker = lang === "pt" ? "Eduboxx Primeiro" : lang === "es" ? "Eduboxx Primero" : "Eduboxx First";

  return (
    <div className="page-shell">
      <nav className="nav">
        <div className="container nav-inner">
          <div className="brand-wrap">
            <div className="brand">DatanexAI</div>
            <div className="brand-sub">Brazil Master Licensing • Eduboxx V5</div>
          </div>

          <div className="nav-links">
            <a href="#proof">{t.nav.proof}</a>
            <a href="#problem">{t.nav.problem}</a>
            <a href="#solution">{t.nav.solution}</a>
            <a href="#products">{t.nav.products}</a>
            <a href="#flow">{t.nav.flow}</a>
            <a href="#impact">{t.nav.impact}</a>
            <a href="#cases">{t.nav.cases}</a>
            <a href="#licensing">{t.nav.licensing}</a>
            <a href="#contact">{t.nav.contact}</a>

            <div className="lang-switcher" aria-label="Language switcher">
              <button type="button" className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => setLang("en")}>EN</button>
              <button type="button" className={`lang-btn ${lang === "pt" ? "active" : ""}`} onClick={() => setLang("pt")}>PT</button>
              <button type="button" className={`lang-btn ${lang === "es" ? "active" : ""}`} onClick={() => setLang("es")}>ES</button>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <div className="eyebrow">{t.hero.eyebrow}</div>
              <h1>{t.hero.title}</h1>
              <p className="lead">{t.hero.subtitle}</p>

              <div className="cta-row">
                <a className="btn btn-primary" href="#contact">{t.hero.primaryCta}</a>
                <a className="btn btn-secondary" href="#licensing">{t.hero.secondaryCta}</a>
              </div>

              <div className="stats-grid">
                {t.hero.stats.map((item) => (
                  <div className="stat-item" key={item}>{item}</div>
                ))}
              </div>
            </div>

            <aside className="glass-card">
              <div className="mini-kicker">{sideKicker}</div>
              <div className="card-title">{t.hero.sideTitle}</div>
              <ul className="bullet-list">
                {t.hero.sideBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        <section id="proof">
          <div className="container">
            <div className="section-header">
              <h2>{t.proof.title}</h2>
              <p>{t.proof.intro}</p>
            </div>

            <div className="proof-grid">
              {t.proof.stats.map((item) => (
                <div className="proof-stat" key={item}>{item}</div>
              ))}
            </div>

            <div className="proof-band">
              <div className="mini-kicker">{t.proof.institutionsTitle}</div>
              <div className="institutions-row">
                {t.proof.institutions.map((institution) => (
                  <div className="institution-pill" key={institution}>{institution}</div>
                ))}
              </div>
              <p className="proof-description">{t.proof.description}</p>
            </div>
          </div>
        </section>

        <section id="problem">
          <div className="container">
            <div className="section-header">
              <h2>{t.problem.title}</h2>
              <p>{t.problem.intro}</p>
            </div>
            <div className="cards-4">
              {t.problem.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="solution">
          <div className="container">
            <div className="section-header">
              <h2>{t.solution.title}</h2>
              <p>{t.solution.intro}</p>
            </div>
            <div className="cards-4">
              {t.solution.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="products">
          <div className="container">
            <div className="section-header">
              <h2>{t.products.title}</h2>
              <p>{t.products.intro}</p>
            </div>

            <div className="cards-4">
              {t.products.cards.map((card) => (
                <article className="feature-card" key={card.name}>
                  <span className="feature-tag">{card.tag}</span>
                  <h3>{card.name}</h3>
                  <ul className="feature-list">
                    {card.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <p className="section-closing">{t.products.closing}</p>
          </div>
        </section>

        <section id="flow">
          <div className="container">
            <div className="section-header">
              <h2>{t.flow.title}</h2>
              <p>{t.flow.intro}</p>
            </div>
            <div className="steps-grid">
              {t.flow.steps.map((step) => (
                <article className="step-card" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="impact">
          <div className="container">
            <div className="section-header">
              <h2>{t.impact.title}</h2>
              <p>{t.impact.intro}</p>
            </div>
            <div className="cards-4">
              {t.impact.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="cases">
          <div className="container">
            <div className="section-header">
              <h2>{t.cases.title}</h2>
              <p>{t.cases.intro}</p>
            </div>

            <div className="cases-grid">
              {t.cases.cards.map((card) => (
                <article className="case-card" key={card.title}>
                  <h3>{card.title}</h3>
                  <div className="before-after">
                    <div className="before-box">
                      <span className="before-label">{beforeLabel}</span>
                      {card.before}
                    </div>
                    <div className="after-box">
                      <span className="after-label">{afterLabel}</span>
                      {card.after}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="licensing">
          <div className="container">
            <div className="section-header">
              <h2>{t.licensing.title}</h2>
            </div>
            <div className="licensing-box">
              <p>{t.licensing.body}</p>
              <div className="licensing-points">
                {t.licensing.bullets.map((bullet) => (
                  <div className="licensing-point" key={bullet}>{bullet}</div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="container contact-grid">
            <div className="contact-card">
              <h3>{t.contact.title}</h3>
              <p>{t.contact.intro}</p>
              <p className="form-note">{t.contact.note}</p>
            </div>

            <div className="contact-card">
              <form onSubmit={handleSubmit}>
                <div className="field-grid">
                  <label>
                    {t.contact.form.name}
                    <input name="name" value={formData.name} onChange={handleChange} placeholder={t.contact.form.name} required />
                  </label>

                  <label>
                    {t.contact.form.org}
                    <input name="org" value={formData.org} onChange={handleChange} placeholder={t.contact.form.org} required />
                  </label>
                </div>

                <div className="field-grid">
                  <label>
                    {t.contact.form.email}
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t.contact.form.email} required />
                  </label>

                  <label>
                    {t.contact.form.region}
                    <input name="region" value={formData.region} onChange={handleChange} placeholder={t.contact.form.region} />
                  </label>
                </div>

                <label>
                  {t.contact.form.interest}
                  <select name="interest" value={formData.interest} onChange={handleChange} required>
                    <option value="">Select...</option>
                    {t.contact.form.options.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>

                <label>
                  {t.contact.form.message}
                  <textarea name="message" value={formData.message} onChange={handleChange} placeholder={t.contact.form.message} />
                </label>

                <button className="btn btn-primary" type="submit">{t.contact.form.submit}</button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-box">
          <div>{t.footer.left}</div>
          <div>{t.footer.right}</div>
        </div>
      </footer>
    </div>
  );
}